/**
 * Which cloud rows a surface shows: connected Checkouts, Registrations not yet
 * connected on this device, and orphaned inventory. One answer, read by the
 * sidebar and the `/new` picker with a `surface` parameter for their one real
 * difference — see CONTEXT.md, "Visible cloud rows".
 *
 * Before this, "which rows are visible" was derived twice. The sidebar's
 * pre-connect half (`resolveVisiblePreConnectCloudRows`, now deleted) excluded
 * a catalog row whenever its `runtimeId` appeared among a set of "already
 * connected" runtime ids. A Forge Cloud runtime is per-owner and hosts every
 * Registration that owner has, so once ONE of them connected, every sibling
 * Registration on that same runtime vanished from the pre-connect list too —
 * exactly the runtime-level exclusion FOR-36359 removed everywhere else in
 * favour of Registration identity.
 *
 * The fix is `resolveRepresentedCatalogRows`, below: it decides "is THIS row
 * already represented" over every Registration a caller's known projects
 * actually name — their own identity, plus (for the sidebar) every worktree
 * member and member ref — using `findNamingCatalogRow`
 * (`components/projects/pickableProject.ts`, the same precise matcher
 * `isPickableProject` builds on), falling back to a per-environment count
 * only where rows genuinely cannot be told apart. See that function's doc
 * comment for why a single pass over-claims.
 */
import type { ScopedProjectRef } from "@cmmd-forge/contracts";
import type { CloudProjectCatalogRow } from "../../cmmd/cmmdForgeRuntimeListResource";
import { classifyEnvironmentId } from "../../environments/environmentIdClassification";
import type { ProjectImportJob } from "../../projectImportStore";
import { cloudProjectIsSuppressedByVisibleJob } from "../sidebar/cloudProjectImportJobs.logic";
import { toRegisteringCatalogRow } from "../sidebar/cloudProjectsGroup.logic";
import {
  buildVisibleConnectedCloudProjects,
  type BuildVisibleConnectedCloudProjectsInput,
} from "../sidebar/connectedCloudProjects.logic";
import type { SidebarProjectSnapshot } from "../sidebar/SidebarProjectItem";
import { findNamingCatalogRow, type RegistrableProjectIdentity } from "./pickableProject";

/**
 * The sidebar renders pre-connect rows and an orphaned-inventory fallback
 * alongside connected Checkouts. The `/new` picker only ever targets a
 * Registration — it has no client-store residents to fall back to when the
 * catalog cannot be reached, and it builds its own connected-project list from
 * the app's live `Project[]`, not `SidebarProjectSnapshot[]` — so it asks this
 * module only for `preConnectCloudRows` and leaves the other two empty.
 */
export type VisibleCloudRowsSurface = "sidebar" | "draft-picker";

export interface VisibleCloudRowsInput {
  /** Every catalog row for this account, resolvedRef included. */
  readonly catalogRows: readonly CloudProjectCatalogRow[];
  readonly catalogMembershipIsAuthoritative: boolean;
  /** Canonical archived boards. No surface offers a row on one. */
  readonly archivedCmmdProjectIds: ReadonlySet<number>;
  /**
   * Every Registration a caller's own known projects already name, so a
   * catalog row matching one of them is not offered again as pre-connect.
   * Built with {@link collectRepresentedProjectIdentities} on the sidebar; the
   * `/new` picker has no worktree members to fan out and maps its `Project[]`
   * directly.
   */
  readonly representedProjectIdentities: readonly RegistrableProjectIdentity[];
  /** Visible import jobs whose own clone already accounts for a row. */
  readonly suppressingProjectImportJobs: readonly ProjectImportJob[];
  readonly surface: VisibleCloudRowsSurface;
  /** Sidebar-only: unused, and safely omitted, on `"draft-picker"`. */
  readonly connectedProjectByRuntimeId?: ReadonlyMap<string, readonly SidebarProjectSnapshot[]>;
  readonly inventoryRefsByRuntimeId?: ReadonlyMap<string, readonly ScopedProjectRef[]>;
  readonly inventoryProjects?: readonly SidebarProjectSnapshot[];
}

export interface VisibleCloudRowsResult {
  /** Sidebar-only. Empty on `"draft-picker"`. */
  readonly connectedProjects: readonly SidebarProjectSnapshot[];
  readonly preConnectCloudRows: readonly CloudProjectCatalogRow[];
  /** Sidebar-only. Empty on `"draft-picker"`. */
  readonly orphanedInventoryProjects: readonly SidebarProjectSnapshot[];
}

/**
 * Every Registration a sidebar project already speaks for: its own identity,
 * every worktree member (a mixed local+cloud project's cloud member has no
 * `resolvedRef` of its own to match against, only a `cwd`), and every member
 * ref merged in from CMMD's runtime inventory. `connectedProjects` and
 * `mergedProjects` (the sidebar's local + mixed rows) both belong in here —
 * an inventory-only project does NOT, since one of those is exactly what a
 * pre-connect row still has to carry member refs for (see
 * `apps/web/src/components/sidebar/projectGroupedSidebar.logic.ts`, which
 * merges `preConnectCloudMemberRefsByRuntimeId` onto the very row this
 * function might otherwise have suppressed).
 */
export function collectRepresentedProjectIdentities(
  projects: readonly SidebarProjectSnapshot[],
): readonly RegistrableProjectIdentity[] {
  const identities: RegistrableProjectIdentity[] = [];
  for (const project of projects) {
    identities.push({
      environmentId: project.environmentId,
      projectId: project.id,
      cwd: project.cwd,
    });
    for (const member of project.worktreeMembers) {
      identities.push({
        environmentId: member.environmentId,
        projectId: member.projectId,
        cwd: member.cwd,
      });
    }
    for (const ref of project.memberProjectRefs) {
      identities.push({ environmentId: ref.environmentId, projectId: ref.projectId });
    }
  }
  return identities;
}

/**
 * One identity, keyed the way the catalog keys its rows.
 *
 * A durable `env_` Environment reaches a surface under its own key while the
 * catalog rows are keyed by the legacy locator, so a durable identity names no
 * row and its Registration is offered a second time as a pre-connect row, right
 * next to the connected project already speaking for it. The row itself says
 * which two keys are the same Sprite, through `cmmdEnvironmentId`, so the alias
 * belongs here: both passes below keep working on one key space instead of each
 * learning about the second shape.
 */
function aliasIdentityToCatalogEnvironment(
  identity: RegistrableProjectIdentity,
  registeringRows: readonly {
    readonly row: CloudProjectCatalogRow;
    readonly environmentId: string | null;
  }[],
): RegistrableProjectIdentity {
  const durable = registeringRows.find(
    (candidate) =>
      candidate.row.cmmdEnvironmentId != null &&
      candidate.row.cmmdEnvironmentId === identity.environmentId,
  );
  return durable?.environmentId == null
    ? identity
    : { ...identity, environmentId: durable.environmentId };
}

/**
 * The catalog rows already named by one of `identities`, matched by
 * Registration — never by runtime.
 *
 * Two passes, because a single-pass "ask `findRegisteringCatalogRow` per
 * identity" over-claims: on a per-owner runtime whose rows cannot be told
 * apart (no `resolvedRef`, a shared sprite-root `workspaceRoot`), that
 * matcher's fallback answers every identity with the SAME first row, so two
 * residents on one ambiguous runtime would both "claim" one row and leave its
 * sibling looking unrepresented — visible in `projectPickerParity.test.ts`'s
 * "both keep every project when the rows all report the sprite root" case,
 * which regressed to offering "other" twice under a naive single pass.
 *
 * Pass one is precise: {@link findNamingCatalogRow} only matches a
 * `resolvedRef` or a `workspaceRoot` no sibling on the row's environment
 * shares, so a match here IS this identity's row, and nothing else on that
 * environment could be mistaken for it.
 *
 * Pass two is a count, not an identity match, and deliberately so: for every
 * environment where some identities went unmatched, it claims that many of
 * the environment's remaining (ambiguous, or single-and-still-unmatched) rows
 * — arbitrarily which ones, since nothing can tell them apart — mirroring the
 * same claim `pickerProjectSummaries.ts`'s ledger already makes for the `/new`
 * picker's own connected-project de-dup.
 */
function resolveRepresentedCatalogRows(
  identities: readonly RegistrableProjectIdentity[],
  rows: readonly CloudProjectCatalogRow[],
): ReadonlySet<CloudProjectCatalogRow> {
  const registeringRows = rows.map((row) => ({ row, ...toRegisteringCatalogRow(row) }));
  const represented = new Set<CloudProjectCatalogRow>();
  const unresolvedCountByEnvironmentId = new Map<string, number>();

  for (const rawIdentity of identities) {
    const identity = aliasIdentityToCatalogEnvironment(rawIdentity, registeringRows);
    const named = findNamingCatalogRow(registeringRows, identity);
    if (named) {
      represented.add(named.row);
      continue;
    }
    const hasRowsOnEnvironment = registeringRows.some(
      (candidate) => candidate.environmentId === identity.environmentId,
    );
    if (!hasRowsOnEnvironment) continue;
    unresolvedCountByEnvironmentId.set(
      identity.environmentId,
      (unresolvedCountByEnvironmentId.get(identity.environmentId) ?? 0) + 1,
    );
  }

  for (const [environmentId, unresolvedCount] of unresolvedCountByEnvironmentId) {
    const stillUnclaimed = registeringRows.filter(
      (candidate) => candidate.environmentId === environmentId && !represented.has(candidate.row),
    );
    for (let index = 0; index < Math.min(unresolvedCount, stillUnclaimed.length); index += 1) {
      represented.add(stillUnclaimed[index]!.row);
    }
  }

  return represented;
}

function resolveOrphanedInventoryProjects(input: {
  readonly inventoryProjects: readonly SidebarProjectSnapshot[];
  readonly liveCatalogRows: readonly CloudProjectCatalogRow[];
  readonly catalogMembershipIsAuthoritative: boolean;
  readonly connectedProjectByRuntimeId: ReadonlyMap<string, readonly SidebarProjectSnapshot[]>;
}): readonly SidebarProjectSnapshot[] {
  // An authoritative catalog is a complete account of every Registration —
  // nothing it fails to list is a project the inventory can rescue.
  if (input.catalogMembershipIsAuthoritative) return [];
  const catalogRuntimeIds = new Set(
    input.liveCatalogRows.flatMap((row) => (row.runtimeId === null ? [] : [row.runtimeId])),
  );
  return input.inventoryProjects.filter((project) => {
    const runtimeId = classifyEnvironmentId(project.environmentId).legacyRuntimeId;
    return (
      runtimeId !== null &&
      !catalogRuntimeIds.has(runtimeId) &&
      !input.connectedProjectByRuntimeId.has(runtimeId)
    );
  });
}

export function resolveVisibleCloudRows(input: VisibleCloudRowsInput): VisibleCloudRowsResult {
  const liveCatalogRows = input.catalogRows.filter(
    (row) => !input.archivedCmmdProjectIds.has(row.cmmdProjectId),
  );
  const represented = resolveRepresentedCatalogRows(
    input.representedProjectIdentities,
    liveCatalogRows,
  );
  const preConnectCloudRows = liveCatalogRows.filter(
    (row) =>
      !represented.has(row) &&
      !cloudProjectIsSuppressedByVisibleJob(
        row.projectName,
        row.githubRepoFullName,
        input.suppressingProjectImportJobs,
      ),
  );

  if (input.surface === "draft-picker") {
    return { connectedProjects: [], preConnectCloudRows, orphanedInventoryProjects: [] };
  }

  const connectedProjectByRuntimeId = input.connectedProjectByRuntimeId ?? new Map();
  const connectedInput: BuildVisibleConnectedCloudProjectsInput = {
    connectedProjectByRuntimeId,
    additionalRefsByRuntimeId: input.inventoryRefsByRuntimeId ?? new Map(),
    catalogRows: liveCatalogRows,
    catalogMembershipIsAuthoritative: input.catalogMembershipIsAuthoritative,
    projectIsSuppressed: () => false,
  };
  const connectedProjects = buildVisibleConnectedCloudProjects(connectedInput);

  const orphanedInventoryProjects = resolveOrphanedInventoryProjects({
    inventoryProjects: input.inventoryProjects ?? [],
    liveCatalogRows,
    catalogMembershipIsAuthoritative: input.catalogMembershipIsAuthoritative,
    connectedProjectByRuntimeId,
  });

  return { connectedProjects, preConnectCloudRows, orphanedInventoryProjects };
}
